// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 17.9.0 — Main tour orchestrator.
 * Wraps driver.js + action engine with floating TourController.
 */

import { useEffect, useCallback, useMemo } from 'react';
import type { TourScript } from '@vn-grc/shared';
import { useTourEngine, resolveTourScriptTitle, type TourMode } from '../hooks/useTourEngine.js';
import { useLocale } from '../hooks/useLocale.js';
import { TourController } from './TourController.js';
import './tour-runner.css';

interface TourRunnerProps {
  script: TourScript | null;
  mode?: TourMode;
  startStepIndex?: number;
  onComplete?: (module: string) => void;
  onExit?: () => void;
}

export function TourRunner({
  script,
  mode,
  startStepIndex = 0,
  onComplete,
  onExit,
}: TourRunnerProps) {
  const [state, actions] = useTourEngine();
  const { locale } = useLocale();
  const { status, currentStepIndex, mode: engineMode, speed, script: activeScript } = state;

  // Fix-sprint 20260419-1455 F6 — handleExit moved above effects that
  // reference it so it can be a declared dep without TDZ issues.
  const handleExit = useCallback(() => {
    actions.stop();
    onExit?.();
  }, [actions, onExit]);

  // Start tour when script changes
  useEffect(() => {
    if (script && status === 'idle') {
      actions.start(script, mode, startStepIndex);
    }
    // Fix-sprint 20260419-1455 F6 — full dep list.
  }, [script, status, actions, mode, startStepIndex]);

  // Handle tour finished
  useEffect(() => {
    if (status === 'finished' && activeScript) {
      onComplete?.(activeScript.module);
    }
    // Fix-sprint 20260419-1455 F6 — include activeScript + onComplete.
  }, [status, activeScript, onComplete]);

  // Keyboard shortcuts
  useEffect(() => {
    if (status === 'idle') return undefined;

    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case ' ':
          e.preventDefault();
          if (status === 'paused') actions.resume();
          else if (status === 'running') actions.pause();
          break;
        case 'Escape':
          handleExit();
          break;
        case 'ArrowRight':
          actions.nextStep();
          break;
        case 'ArrowLeft':
          actions.prevStep();
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // Fix-sprint 20260419-1455 F6 — actions is stable from useTourEngine
    // but list it explicitly; handleExit is memoized above.
  }, [status, actions, handleExit]);

  const handlePlayPause = useCallback(() => {
    if (status === 'paused') actions.resume();
    else if (status === 'running') actions.pause();
  }, [status, actions]);

  const stepAnnouncement = useMemo(() => {
    if (!activeScript || status === 'idle' || status === 'finished') return '';
    return `Step ${currentStepIndex + 1} of ${activeScript.steps.length}`;
  }, [activeScript, status, currentStepIndex]);

  if (!activeScript || status === 'idle' || status === 'finished') return null;

  return (
    <>
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {stepAnnouncement}
      </div>
      <TourController
        status={status}
        currentStep={currentStepIndex}
        totalSteps={activeScript.steps.length}
        mode={engineMode}
        speed={speed}
        tourTitle={resolveTourScriptTitle(activeScript, locale)}
        onPlayPause={handlePlayPause}
        onNext={actions.nextStep}
        onPrev={actions.prevStep}
        onSpeedChange={actions.setSpeed}
        onModeChange={actions.setMode}
        onExit={handleExit}
      />
    </>
  );
}
